import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, of, tap, throwError } from 'rxjs';
import type { IdpV2Endpoint } from '../models/idp-v2-endpoint.model';
import { DocumentEndpointService } from '../service/document-endpoint.service';
import { mapDocumentEndpoints } from '../utils/endpoint-mapper';
import { groupEndpointsByCategory } from '../utils/endpoint-mapper';

@Injectable({ providedIn: 'root' })
export class IntegrationV2EndpointsStore {
  private readonly api = inject(DocumentEndpointService);

  readonly loading = signal(false);
  readonly loaded = signal(false);
  readonly error = signal<string | null>(null);
  readonly endpoints = signal<IdpV2Endpoint[]>([]);
  readonly configurableConfigurationAllowed = signal(false);

  readonly total = computed(() => this.endpoints().length);
  readonly grouped = computed(() => groupEndpointsByCategory(this.endpoints()));

  /** Suppress fetch errors when the empty-assignment state is shown; keep auth errors. */
  readonly shellError = computed(() => {
    const err = this.error();
    if (!err) return null;
    if (this.total() > 0) return err;
    return this.isAuthErrorMessage(err) ? err : null;
  });

  ensureLoaded(): Observable<IdpV2Endpoint[]> {
    if (this.loaded() && !this.error()) {
      return of(this.endpoints());
    }
    return this.load();
  }

  load(): Observable<IdpV2Endpoint[]> {
    this.loading.set(true);
    this.error.set(null);

    return this.api.list().pipe(
      tap(data => {
        this.configurableConfigurationAllowed.set(
          Boolean(data?.configurableConfigurationAllowed),
        );
      }),
      map(data => mapDocumentEndpoints(data?.items ?? [])),
      tap(items => {
        this.endpoints.set(items);
        this.loaded.set(true);
        this.loading.set(false);
        this.error.set(null);
      }),
      catchError((err: HttpErrorResponse) => {
        this.loading.set(false);
        this.error.set(this.extractError(err));
        return throwError(() => err);
      }),
    );
  }

  findBySlug(slug: string): IdpV2Endpoint | undefined {
    return this.endpoints().find(e => e.slug === slug);
  }

  private extractError(err: HttpErrorResponse): string {
    const bodyMessage = this.readApiErrorMessage(err);
    if (bodyMessage) return bodyMessage;
    if (err.status === 401) return 'Authentication required. Please sign in again.';
    return 'Failed to load assigned document endpoints. Please try again.';
  }

  private readApiErrorMessage(err: HttpErrorResponse): string | null {
    if (!err.error || typeof err.error !== 'object') return null;
    const body = err.error as Record<string, unknown>;
    const message = body['message'];
    return typeof message === 'string' && message ? message : null;
  }

  private isAuthErrorMessage(message: string): boolean {
    return message.includes('Authentication') || message.includes('sign in');
  }
}
